export const trigonometryData = {
  title: "Trigonometry",
  description: "Master unit circle, trigonometric identities, laws of sines and cosines, and inverse trigonometric functions",
  skills: [
    {
      id: "unit-circle-special-angles",
      title: "Unit Circle & Special Angles",
      description: "Evaluate trigonometric functions using the unit circle and special angles (0°, 30°, 45°, 60°, 90°, and their multiples)",
      explanation: `
## Understanding the Unit Circle

The **unit circle** is a circle with radius 1 centered at the origin (0, 0) in the coordinate plane. It's fundamental to understanding trigonometric functions.

### Key Concepts:
1. **Coordinates on the Unit Circle**: Any point (x, y) on the unit circle satisfies x² + y² = 1
2. **Angle Measurement**: Angles are measured counterclockwise from the positive x-axis
3. **Trigonometric Functions**:
   - cos(θ) = x-coordinate
   - sin(θ) = y-coordinate
   - tan(θ) = y/x = sin(θ)/cos(θ)

### Special Angles and Their Values:

**Quadrant I (0° to 90°):**
- **0° (0 radians)**: cos(0) = 1, sin(0) = 0, tan(0) = 0
- **30° (π/6)**: cos(30°) = √3/2, sin(30°) = 1/2, tan(30°) = √3/3
- **45° (π/4)**: cos(45°) = √2/2, sin(45°) = √2/2, tan(45°) = 1
- **60° (π/3)**: cos(60°) = 1/2, sin(60°) = √3/2, tan(60°) = √3
- **90° (π/2)**: cos(90°) = 0, sin(90°) = 1, tan(90°) = undefined

### Reference Angles:
For angles in other quadrants, use the reference angle (acute angle to the x-axis) and apply sign rules:
- **Quadrant II**: sin is positive, cos and tan are negative
- **Quadrant III**: tan is positive, sin and cos are negative
- **Quadrant IV**: cos is positive, sin and tan are negative

**Memory Aid: "All Students Take Calculus"**
- **A**ll (Quadrant I): All positive
- **S**tudents (Quadrant II): Sin positive
- **T**ake (Quadrant III): Tan positive
- **C**alculus (Quadrant IV): Cos positive

### Example:
Find sin(150°):
1. Reference angle: 180° - 150° = 30°
2. Quadrant II: sin is positive
3. sin(150°) = sin(30°) = 1/2
      `,
      questions: [
        {
          id: 1,
          question: "What is the exact value of cos(π/3)?",
          options: ["1/2", "√3/2", "√2/2", "1"],
          correctAnswer: 0,
          solution: `
**Step 1:** Recognize that π/3 radians = 60°

**Step 2:** Recall the special angle values for 60°:
- cos(60°) = 1/2
- sin(60°) = √3/2

**Step 3:** Therefore, cos(π/3) = **1/2**

**Answer: 1/2**
          `
        },
        {
          id: 2,
          question: "What is sin(5π/4)?",
          options: ["-√2/2", "√2/2", "-1/2", "1/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the quadrant
- 5π/4 is between π and 3π/2, so it's in Quadrant III

**Step 2:** Find the reference angle
- Reference angle = 5π/4 - π = π/4 (or 45°)

**Step 3:** Determine the sign
- In Quadrant III, sine is negative

**Step 4:** Calculate
- sin(π/4) = √2/2
- Therefore, sin(5π/4) = **-√2/2**

**Answer: -√2/2**
          `
        },
        {
          id: 3,
          question: "What is tan(7π/6)?",
          options: ["√3/3", "-√3/3", "√3", "-√3"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the quadrant
- 7π/6 is between π and 3π/2, so it's in Quadrant III

**Step 2:** Find the reference angle
- Reference angle = 7π/6 - π = π/6 (or 30°)

**Step 3:** Determine the sign
- In Quadrant III, tangent is positive

**Step 4:** Calculate
- tan(π/6) = sin(π/6)/cos(π/6) = (1/2)/(√3/2) = 1/√3 = √3/3
- Therefore, tan(7π/6) = **√3/3**

**Answer: √3/3**
          `
        },
        {
          id: 4,
          question: "If cos(θ) = -1/2 and θ is in Quadrant II, what is sin(θ)?",
          options: ["√3/2", "-√3/2", "1/2", "-1/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use the Pythagorean identity
- sin²(θ) + cos²(θ) = 1

**Step 2:** Substitute cos(θ) = -1/2
- sin²(θ) + (-1/2)² = 1
- sin²(θ) + 1/4 = 1
- sin²(θ) = 3/4

**Step 3:** Solve for sin(θ)
- sin(θ) = ±√(3/4) = ±√3/2

**Step 4:** Determine the sign
- In Quadrant II, sine is positive
- Therefore, sin(θ) = **√3/2**

**Answer: √3/2**
          `
        },
        {
          id: 5,
          question: "What is the exact value of sin(11π/6)?",
          options: ["-1/2", "1/2", "-√3/2", "√3/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the quadrant
- 11π/6 is between 3π/2 and 2π, so it's in Quadrant IV

**Step 2:** Find the reference angle
- Reference angle = 2π - 11π/6 = π/6 (or 30°)

**Step 3:** Determine the sign
- In Quadrant IV, sine is negative

**Step 4:** Calculate
- sin(π/6) = 1/2
- Therefore, sin(11π/6) = **-1/2**

**Answer: -1/2**
          `
        },
        {
          id: 6,
          question: "What is cos(3π/4)?",
          options: ["-√2/2", "√2/2", "-1/2", "1/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the quadrant
- 3π/4 is between π/2 and π, so it's in Quadrant II

**Step 2:** Find the reference angle
- Reference angle = π - 3π/4 = π/4 (or 45°)

**Step 3:** Determine the sign
- In Quadrant II, cosine is negative

**Step 4:** Calculate
- cos(π/4) = √2/2
- Therefore, cos(3π/4) = **-√2/2**

**Answer: -√2/2**
          `
        },
        {
          id: 7,
          question: "If sin(θ) = 3/5 and θ is in Quadrant I, what is cos(θ)?",
          options: ["4/5", "-4/5", "3/5", "-3/5"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use the Pythagorean identity
- sin²(θ) + cos²(θ) = 1

**Step 2:** Substitute sin(θ) = 3/5
- (3/5)² + cos²(θ) = 1
- 9/25 + cos²(θ) = 1
- cos²(θ) = 16/25

**Step 3:** Solve for cos(θ)
- cos(θ) = ±4/5

**Step 4:** Determine the sign
- In Quadrant I, cosine is positive
- Therefore, cos(θ) = **4/5**

**Answer: 4/5**
          `
        },
        {
          id: 8,
          question: "What is tan(π)?",
          options: ["0", "1", "-1", "undefined"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the angle
- π radians = 180°

**Step 2:** Find the coordinates on the unit circle
- At 180°, the point is (-1, 0)

**Step 3:** Calculate tangent
- tan(θ) = y/x = 0/(-1) = **0**

**Answer: 0**
          `
        },
        {
          id: 9,
          question: "What is the exact value of cos(5π/6)?",
          options: ["-√3/2", "√3/2", "-1/2", "1/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the quadrant
- 5π/6 is between π/2 and π, so it's in Quadrant II

**Step 2:** Find the reference angle
- Reference angle = π - 5π/6 = π/6 (or 30°)

**Step 3:** Determine the sign
- In Quadrant II, cosine is negative

**Step 4:** Calculate
- cos(π/6) = √3/2
- Therefore, cos(5π/6) = **-√3/2**

**Answer: -√3/2**
          `
        },
        {
          id: 10,
          question: "If tan(θ) = -1 and θ is in Quadrant IV, what is the value of θ in radians (0 ≤ θ < 2π)?",
          options: ["7π/4", "3π/4", "5π/4", "π/4"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify where tan = -1
- tan(θ) = -1 when the reference angle is π/4 (45°)

**Step 2:** Determine quadrants where tan is negative
- Quadrants II and IV

**Step 3:** Since θ is in Quadrant IV
- θ = 2π - π/4 = 8π/4 - π/4 = **7π/4**

**Verification:**
- tan(7π/4) = sin(7π/4)/cos(7π/4) = (-√2/2)/(√2/2) = -1 ✓

**Answer: 7π/4**
          `
        }
      ]
    },
    {
      id: "trig-identities",
      title: "Trigonometric Identities",
      description: "Prove and apply sum/difference formulas, double-angle identities, and other fundamental trigonometric identities",
      explanation: `
## Fundamental Trigonometric Identities

### Pythagorean Identities:
1. **sin²(θ) + cos²(θ) = 1**
2. **1 + tan²(θ) = sec²(θ)**
3. **1 + cot²(θ) = csc²(θ)**

### Reciprocal Identities:
- csc(θ) = 1/sin(θ)
- sec(θ) = 1/cos(θ)
- cot(θ) = 1/tan(θ)

### Quotient Identities:
- tan(θ) = sin(θ)/cos(θ)
- cot(θ) = cos(θ)/sin(θ)

### Sum and Difference Formulas:
**Sine:**
- sin(A + B) = sin(A)cos(B) + cos(A)sin(B)
- sin(A - B) = sin(A)cos(B) - cos(A)sin(B)

**Cosine:**
- cos(A + B) = cos(A)cos(B) - sin(A)sin(B)
- cos(A - B) = cos(A)cos(B) + sin(A)sin(B)

**Tangent:**
- tan(A + B) = [tan(A) + tan(B)] / [1 - tan(A)tan(B)]
- tan(A - B) = [tan(A) - tan(B)] / [1 + tan(A)tan(B)]

### Double-Angle Formulas:
**Sine:**
- sin(2θ) = 2sin(θ)cos(θ)

**Cosine (three forms):**
- cos(2θ) = cos²(θ) - sin²(θ)
- cos(2θ) = 2cos²(θ) - 1
- cos(2θ) = 1 - 2sin²(θ)

**Tangent:**
- tan(2θ) = 2tan(θ) / [1 - tan²(θ)]

### Half-Angle Formulas:
- sin(θ/2) = ±√[(1 - cos(θ))/2]
- cos(θ/2) = ±√[(1 + cos(θ))/2]
- tan(θ/2) = ±√[(1 - cos(θ))/(1 + cos(θ))] = sin(θ)/(1 + cos(θ))

### Power-Reducing Formulas:
- sin²(θ) = (1 - cos(2θ))/2
- cos²(θ) = (1 + cos(2θ))/2
- tan²(θ) = (1 - cos(2θ))/(1 + cos(2θ))

### Strategy for Proving Identities:
1. Start with the more complicated side
2. Use fundamental identities to simplify
3. Factor or combine fractions when possible
4. Convert everything to sine and cosine if stuck
5. Work toward the other side of the equation
      `,
      questions: [
        {
          id: 11,
          question: "Simplify: sin²(x) + cos²(x) + tan²(x)",
          options: ["1 + tan²(x)", "sec²(x)", "1 + sin²(x)", "2"],
          correctAnswer: 1,
          solution: `
**Step 1:** Apply the Pythagorean identity
- sin²(x) + cos²(x) = 1

**Step 2:** Substitute into the expression
- sin²(x) + cos²(x) + tan²(x) = 1 + tan²(x)

**Step 3:** Apply another Pythagorean identity
- 1 + tan²(x) = **sec²(x)**

**Answer: sec²(x)**
          `
        },
        {
          id: 12,
          question: "What is sin(75°) using sum formulas? (75° = 45° + 30°)",
          options: ["(√6 + √2)/4", "(√6 - √2)/4", "(√3 + 1)/2", "(√3 - 1)/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use the sum formula for sine
- sin(A + B) = sin(A)cos(B) + cos(A)sin(B)

**Step 2:** Apply with A = 45° and B = 30°
- sin(75°) = sin(45°)cos(30°) + cos(45°)sin(30°)

**Step 3:** Substitute special angle values
- sin(45°) = √2/2, cos(45°) = √2/2
- sin(30°) = 1/2, cos(30°) = √3/2

**Step 4:** Calculate
- sin(75°) = (√2/2)(√3/2) + (√2/2)(1/2)
- = √6/4 + √2/4
- = **(√6 + √2)/4**

**Answer: (√6 + √2)/4**
          `
        },
        {
          id: 13,
          question: "If sin(θ) = 3/5 and θ is in Quadrant I, what is sin(2θ)?",
          options: ["24/25", "7/25", "12/25", "16/25"],
          correctAnswer: 0,
          solution: `
**Step 1:** Find cos(θ) using Pythagorean identity
- sin²(θ) + cos²(θ) = 1
- (3/5)² + cos²(θ) = 1
- cos²(θ) = 16/25
- cos(θ) = 4/5 (positive in Quadrant I)

**Step 2:** Use double-angle formula for sine
- sin(2θ) = 2sin(θ)cos(θ)

**Step 3:** Substitute values
- sin(2θ) = 2(3/5)(4/5)
- = 2(12/25)
- = **24/25**

**Answer: 24/25**
          `
        },
        {
          id: 14,
          question: "Simplify: (1 - cos(2x))/sin(2x)",
          options: ["tan(x)", "cot(x)", "sin(x)", "cos(x)"],
          correctAnswer: 0,
          solution: `
**Step 1:** Apply double-angle formulas
- cos(2x) = 1 - 2sin²(x)
- sin(2x) = 2sin(x)cos(x)

**Step 2:** Substitute into the expression
- (1 - cos(2x))/sin(2x) = (1 - (1 - 2sin²(x)))/(2sin(x)cos(x))
- = 2sin²(x)/(2sin(x)cos(x))

**Step 3:** Simplify
- = sin(x)/cos(x)
- = **tan(x)**

**Answer: tan(x)**
          `
        },
        {
          id: 15,
          question: "What is cos(15°) using difference formulas? (15° = 45° - 30°)",
          options: ["(√6 + √2)/4", "(√6 - √2)/4", "(√3 + 1)/2", "(√3 - 1)/2"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use the difference formula for cosine
- cos(A - B) = cos(A)cos(B) + sin(A)sin(B)

**Step 2:** Apply with A = 45° and B = 30°
- cos(15°) = cos(45°)cos(30°) + sin(45°)sin(30°)

**Step 3:** Substitute special angle values
- cos(45°) = √2/2, sin(45°) = √2/2
- cos(30°) = √3/2, sin(30°) = 1/2

**Step 4:** Calculate
- cos(15°) = (√2/2)(√3/2) + (√2/2)(1/2)
- = √6/4 + √2/4
- = **(√6 + √2)/4**

**Answer: (√6 + √2)/4**
          `
        },
        {
          id: 16,
          question: "Prove: (sin(x) + cos(x))² = 1 + sin(2x). What is the key step?",
          options: ["Expand and use sin(2x) = 2sin(x)cos(x)", "Use double-angle formula for cosine", "Factor out sin(x)", "Use half-angle formula"],
          correctAnswer: 0,
          solution: `
**Step 1:** Expand the left side
- (sin(x) + cos(x))² = sin²(x) + 2sin(x)cos(x) + cos²(x)

**Step 2:** Rearrange terms
- = (sin²(x) + cos²(x)) + 2sin(x)cos(x)

**Step 3:** Apply Pythagorean identity
- = 1 + 2sin(x)cos(x)

**Step 4:** Apply double-angle formula
- = 1 + sin(2x) ✓

**Key step: Expand and use sin(2x) = 2sin(x)cos(x)**

**Answer: Expand and use sin(2x) = 2sin(x)cos(x)**
          `
        },
        {
          id: 17,
          question: "If cos(θ) = 4/5 and θ is in Quadrant IV, what is cos(2θ)?",
          options: ["7/25", "-7/25", "24/25", "-24/25"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use double-angle formula for cosine
- cos(2θ) = 2cos²(θ) - 1

**Step 2:** Substitute cos(θ) = 4/5
- cos(2θ) = 2(4/5)² - 1
- = 2(16/25) - 1
- = 32/25 - 25/25
- = **7/25**

**Note:** The quadrant doesn't affect this calculation since we're using cos²(θ)

**Answer: 7/25**
          `
        },
        {
          id: 18,
          question: "Simplify: cos⁴(x) - sin⁴(x)",
          options: ["cos(2x)", "sin(2x)", "1", "cos²(x)"],
          correctAnswer: 0,
          solution: `
**Step 1:** Factor as difference of squares
- cos⁴(x) - sin⁴(x) = (cos²(x) + sin²(x))(cos²(x) - sin²(x))

**Step 2:** Apply Pythagorean identity
- = (1)(cos²(x) - sin²(x))
- = cos²(x) - sin²(x)

**Step 3:** Recognize double-angle formula
- cos(2x) = cos²(x) - sin²(x)

**Therefore:** cos⁴(x) - sin⁴(x) = **cos(2x)**

**Answer: cos(2x)**
          `
        },
        {
          id: 19,
          question: "What is tan(π/12) using the half-angle formula? (π/12 = (π/6)/2)",
          options: ["2 - √3", "2 + √3", "√3 - 1", "√3 + 1"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use half-angle formula for tangent
- tan(θ/2) = sin(θ)/(1 + cos(θ))

**Step 2:** Apply with θ = π/6
- tan(π/12) = sin(π/6)/(1 + cos(π/6))

**Step 3:** Substitute special angle values
- sin(π/6) = 1/2
- cos(π/6) = √3/2

**Step 4:** Calculate
- tan(π/12) = (1/2)/(1 + √3/2)
- = (1/2)/((2 + √3)/2)
- = 1/(2 + √3)

**Step 5:** Rationalize
- = 1/(2 + √3) × (2 - √3)/(2 - √3)
- = (2 - √3)/(4 - 3)
- = **2 - √3**

**Answer: 2 - √3**
          `
        },
        {
          id: 20,
          question: "Simplify: (sin(x))/(1 + cos(x)) + (1 + cos(x))/(sin(x))",
          options: ["2/sin(x)", "2/cos(x)", "2tan(x)", "2cot(x)"],
          correctAnswer: 0,
          solution: `
**Step 1:** Find common denominator
- = [sin²(x) + (1 + cos(x))²] / [sin(x)(1 + cos(x))]

**Step 2:** Expand numerator
- = [sin²(x) + 1 + 2cos(x) + cos²(x)] / [sin(x)(1 + cos(x))]

**Step 3:** Apply Pythagorean identity
- = [(sin²(x) + cos²(x)) + 1 + 2cos(x)] / [sin(x)(1 + cos(x))]
- = [1 + 1 + 2cos(x)] / [sin(x)(1 + cos(x))]
- = [2 + 2cos(x)] / [sin(x)(1 + cos(x))]

**Step 4:** Factor and simplify
- = [2(1 + cos(x))] / [sin(x)(1 + cos(x))]
- = **2/sin(x)**

**Answer: 2/sin(x)**
          `
        }
      ]
    },
    {
      id: "law-of-sines-cosines",
      title: "Law of Sines & Law of Cosines",
      description: "Apply the Law of Sines and Law of Cosines to solve triangles, including the ambiguous case (SSA)",
      explanation: `
## Law of Sines and Law of Cosines

### Law of Sines:
For any triangle with sides a, b, c opposite to angles A, B, C:

**a/sin(A) = b/sin(B) = c/sin(C)**

Or equivalently:
**sin(A)/a = sin(B)/b = sin(C)/c**

**When to Use:**
- When you know two angles and one side (AAS or ASA)
- When you know two sides and an angle opposite one of them (SSA - ambiguous case)

### Law of Cosines:
For any triangle with sides a, b, c opposite to angles A, B, C:

**a² = b² + c² - 2bc·cos(A)**
**b² = a² + c² - 2ac·cos(B)**
**c² = a² + b² - 2ab·cos(C)**

**When to Use:**
- When you know three sides (SSS)
- When you know two sides and the included angle (SAS)

### The Ambiguous Case (SSA):
When given two sides and an angle opposite one of them, there may be:
- **No solution** (the side opposite the given angle is too short)
- **One solution** (right triangle or the side opposite is longer than the other given side)
- **Two solutions** (two different triangles satisfy the conditions)

**Steps to Check for Ambiguous Case:**
1. Use Law of Sines to find the angle opposite the other given side
2. Check if sin(angle) > 1 → No solution
3. Check if the angle is acute → Possible second solution (supplement angle)
4. Verify both solutions satisfy triangle angle sum = 180°

### Problem-Solving Strategy:

**For SSS (three sides):**
1. Use Law of Cosines to find one angle
2. Use Law of Sines to find a second angle
3. Use angle sum (180°) to find the third angle

**For SAS (two sides and included angle):**
1. Use Law of Cosines to find the third side
2. Use Law of Sines to find another angle
3. Use angle sum to find the third angle

**For AAS or ASA (two angles and a side):**
1. Find the third angle using angle sum
2. Use Law of Sines to find the other sides

**For SSA (two sides and non-included angle):**
1. Use Law of Sines to find another angle
2. Check for ambiguous case
3. Complete the triangle(s)

### Example:
Given triangle ABC with a = 10, b = 8, and angle A = 40°, find angle B.

Using Law of Sines:
- sin(B)/b = sin(A)/a
- sin(B)/8 = sin(40°)/10
- sin(B) = 8·sin(40°)/10 ≈ 0.514
- B ≈ 31° or B ≈ 149°

Check: If B = 149°, then A + B = 189° > 180° (impossible)
Therefore, B ≈ 31° (only one solution in this case)
      `,
      questions: [
        {
          id: 21,
          question: "In triangle ABC, a = 12, b = 15, and angle A = 35°. Find angle B using the Law of Sines.",
          options: ["45.6°", "50.2°", "55.8°", "60.4°"],
          correctAnswer: 0,
          solution: `
**Step 1:** Write the Law of Sines
- sin(A)/a = sin(B)/b

**Step 2:** Substitute known values
- sin(35°)/12 = sin(B)/15

**Step 3:** Solve for sin(B)
- sin(B) = 15·sin(35°)/12
- sin(B) = 15(0.5736)/12
- sin(B) ≈ 0.7170

**Step 4:** Find angle B
- B = arcsin(0.7170)
- B ≈ **45.6°**

**Answer: 45.6°**
          `
        },
        {
          id: 22,
          question: "In triangle ABC, a = 8, b = 10, and c = 12. Find angle C using the Law of Cosines.",
          options: ["82.8°", "75.5°", "68.2°", "90°"],
          correctAnswer: 0,
          solution: `
**Step 1:** Write the Law of Cosines for angle C
- c² = a² + b² - 2ab·cos(C)

**Step 2:** Rearrange to solve for cos(C)
- cos(C) = (a² + b² - c²)/(2ab)

**Step 3:** Substitute values
- cos(C) = (8² + 10² - 12²)/(2·8·10)
- cos(C) = (64 + 100 - 144)/160
- cos(C) = 20/160 = 0.125

**Step 4:** Find angle C
- C = arccos(0.125)
- C ≈ **82.8°**

**Answer: 82.8°**
          `
        },
        {
          id: 23,
          question: "In triangle ABC, a = 7, c = 10, and angle B = 60°. Find side b using the Law of Cosines.",
          options: ["8.9", "9.5", "10.2", "11.0"],
          correctAnswer: 0,
          solution: `
**Step 1:** Write the Law of Cosines for side b
- b² = a² + c² - 2ac·cos(B)

**Step 2:** Substitute known values
- b² = 7² + 10² - 2(7)(10)·cos(60°)
- b² = 49 + 100 - 140(0.5)
- b² = 149 - 70
- b² = 79

**Step 3:** Solve for b
- b = √79
- b ≈ **8.9**

**Answer: 8.9**
          `
        },
        {
          id: 24,
          question: "In triangle ABC, angle A = 45°, angle B = 70°, and side a = 20. Find side b using the Law of Sines.",
          options: ["26.6", "24.8", "22.3", "20.0"],
          correctAnswer: 0,
          solution: `
**Step 1:** Write the Law of Sines
- a/sin(A) = b/sin(B)

**Step 2:** Substitute known values
- 20/sin(45°) = b/sin(70°)

**Step 3:** Solve for b
- b = 20·sin(70°)/sin(45°)
- b = 20(0.9397)/(0.7071)
- b ≈ **26.6**

**Answer: 26.6**
          `
        },
        {
          id: 25,
          question: "In triangle ABC, a = 10, b = 12, and angle A = 30°. How many possible triangles exist? (Ambiguous case)",
          options: ["Two triangles", "One triangle", "No triangle", "Cannot determine"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use Law of Sines to find angle B
- sin(B)/b = sin(A)/a
- sin(B)/12 = sin(30°)/10
- sin(B) = 12(0.5)/10 = 0.6

**Step 2:** Find possible values for B
- B₁ = arcsin(0.6) ≈ 36.9°
- B₂ = 180° - 36.9° = 143.1°

**Step 3:** Check if both are valid
- For B₁ = 36.9°: A + B₁ = 30° + 36.9° = 66.9° < 180° ✓
- For B₂ = 143.1°: A + B₂ = 30° + 143.1° = 173.1° < 180° ✓

**Step 4:** Both solutions are valid
- **Two triangles** exist

**Answer: Two triangles**
          `
        },
        {
          id: 26,
          question: "In triangle ABC, a = 5, b = 7, and c = 10. Find the largest angle.",
          options: ["Angle C ≈ 101.5°", "Angle B ≈ 90°", "Angle A ≈ 85.3°", "Angle C ≈ 95.7°"],
          correctAnswer: 0,
          solution: `
**Step 1:** Identify the largest angle
- The largest angle is opposite the longest side
- Side c = 10 is longest, so angle C is largest

**Step 2:** Use Law of Cosines for angle C
- c² = a² + b² - 2ab·cos(C)
- cos(C) = (a² + b² - c²)/(2ab)

**Step 3:** Substitute values
- cos(C) = (5² + 7² - 10²)/(2·5·7)
- cos(C) = (25 + 49 - 100)/70
- cos(C) = -26/70 ≈ -0.371

**Step 4:** Find angle C
- C = arccos(-0.371)
- C ≈ **101.5°**

**Answer: Angle C ≈ 101.5°**
          `
        },
        {
          id: 27,
          question: "In triangle ABC, angle A = 110°, side b = 8, and side c = 6. Find side a.",
          options: ["12.1", "10.5", "9.8", "8.3"],
          correctAnswer: 0,
          solution: `
**Step 1:** Use Law of Cosines (SAS case)
- a² = b² + c² - 2bc·cos(A)

**Step 2:** Substitute known values
- a² = 8² + 6² - 2(8)(6)·cos(110°)
- a² = 64 + 36 - 96·cos(110°)
- a² = 100 - 96(-0.342)
- a² = 100 + 32.8
- a² = 132.8

**Step 3:** Solve for a
- a = √132.8
- a ≈ **12.1**

**Answer: 12.1**
          `
        },
        {
          id: 28,
          question: "In triangle ABC, a = 15, b = 20, and angle A = 40°. Find angle B. Is there an ambiguous case?",
          options: ["B ≈ 58.99°, no ambiguous case", "B ≈ 58.99° or 121.01°, ambiguous", "B ≈ 50°, no ambiguous case", "No solution exists"],
          correctAnswer: 1,
          solution: `
**Step 1:** Use Law of Sines
- sin(B)/b = sin(A)/a
- sin(B)/20 = sin(40°)/15
- sin(B) = 20·sin(40°)/15 ≈ 0.857

**Step 2:** Find possible angles
- B₁ = arcsin(0.857) ≈ 58.99°
- B₂ = 180° - 58.99° = 121.01°

**Step 3:** Check validity
- For B₁: A + B₁ = 40° + 58.99° = 98.99° < 180° ✓
- For B₂: A + B₂ = 40° + 121.01° = 161.01° < 180° ✓

**Step 4:** Both solutions valid
- **B ≈ 58.99° or 121.01°, ambiguous case exists**

**Answer: B ≈ 58.99° or 121.01°, ambiguous**
          `
        },
        {
          id: 29,
          question: "A triangle has sides 6, 8, and 10. What type of triangle is it?",
          options: ["Right triangle", "Acute triangle", "Obtuse triangle", "Cannot determine"],
          correctAnswer: 0,
          solution: `
**Step 1:** Check if Pythagorean theorem holds
- For a right triangle: a² + b² = c² (where c is the longest side)

**Step 2:** Test with the given sides
- 6² + 8² = 36 + 64 = 100
- 10² = 100

**Step 3:** Compare
- 6² + 8² = 10² ✓

**Conclusion:** The triangle satisfies the Pythagorean theorem
- It is a **right triangle**

**Answer: Right triangle**
          `
        },
        {
          id: 30,
          question: "In triangle ABC, angle A = 50°, angle C = 70°, and side a = 12. Find side c.",
          options: ["14.7", "13.2", "11.8", "10.5"],
          correctAnswer: 0,
          solution: `
**Step 1:** Find angle B
- A + B + C = 180°
- 50° + B + 70° = 180°
- B = 60°

**Step 2:** Use Law of Sines
- a/sin(A) = c/sin(C)

**Step 3:** Substitute values
- 12/sin(50°) = c/sin(70°)

**Step 4:** Solve for c
- c = 12·sin(70°)/sin(50°)
- c = 12(0.9397)/(0.7660)
- c ≈ **14.7**

**Answer: 14.7**
          `
        }
      ]
    }
  ]
};